import { useState } from 'react'
import { Button } from '@design-systems/canvas-ui'
import Icon, {
  CheckmarkIcon,
  ChevronDownIcon,
  CloseIcon,
  DatabaseIcon,
  UploadIcon,
} from '@design-systems/react-icons'
import DuplicateUploadModal from './DuplicateUploadModal'
import ExtractionStatusNotice from './ExtractionStatusNotice'
import { DOCUMENT_STATUS } from '../config/brandDetailsConfig'
import { BRAND_LIFECYCLE_STATUS } from '../config/brandLifecycleConfig'
import { formatFileSize } from '../mappers/brandDocumentMapper'

const MAX_FILE_SIZE_MB = 500
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const ACCEPTED_FILE_TYPES = '.pdf,.docx,.ppt,.pptx'
const ACCEPTED_EXTENSIONS = ACCEPTED_FILE_TYPES.split(',')

const DocumentTypePicker = ({ documentTypes, onChange, value }) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedDocumentType = documentTypes.find(({ id }) => id === value)

  return (
    <div className="upload-documents__type">
      <span id="document-type-label">Document type</span>
      <button
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby="document-type-label document-type-value"
        className="upload-documents__type-toggle"
        id="document-type-value"
        onClick={() => setIsOpen((current) => !current)}
        type="button"
      >
        {selectedDocumentType?.label || 'Select document type'}
        <Icon size="sm" svg={ChevronDownIcon} />
      </button>
      {isOpen && documentTypes.length > 0 && (
        <ul
          aria-labelledby="document-type-label"
          className="upload-documents__type-options"
          role="listbox"
        >
          {[...documentTypes]
            .sort((a, b) => Number(b.required) - Number(a.required))
            .map((documentType) => (
              <li key={documentType.id} role="presentation">
                <button
                  aria-selected={documentType.id === value}
                  onClick={() => {
                    onChange(documentType.id)
                    setIsOpen(false)
                  }}
                  role="option"
                  type="button"
                >
                  <span>
                    {documentType.id === value && (
                      <Icon size="sm" svg={CheckmarkIcon} />
                    )}
                    {documentType.label}
                  </span>
                  {documentType.required && <strong>Required</strong>}
                </button>
              </li>
            ))}
        </ul>
      )}
      {documentTypes.length === 0 && (
        <small className="upload-documents__type-empty">
          No document types are configured yet. Add them in Settings.
        </small>
      )}
    </div>
  )
}

const getStatusText = ({ error, progress, status }) => {
  if (status === DOCUMENT_STATUS.UPLOADING) return `Uploading ${progress}%`
  if (status === DOCUMENT_STATUS.SAVING) return 'Saving to Brand DNA…'
  if (status === DOCUMENT_STATUS.UPLOADED) return 'Uploaded'
  return error || 'Upload failed'
}

// A failed multipart upload keeps the parts AssetHub already confirmed, so say
// how much of the file the retry gets to skip.
const getResumeText = ({ completedChunks = 0, totalChunks = 0 }) =>
  completedChunks > 0 && completedChunks < totalChunks
    ? `${completedChunks} of ${totalChunks} parts already uploaded — retry sends the rest`
    : ''

const UploadProgress = ({
  deletingId,
  onDelete,
  onDismiss,
  onRetry,
  uploadJobs,
}) => (
  <section
    aria-label="Upload progress"
    className="upload-documents__progress-panel"
  >
    <header>
      <h2>Upload progress</h2>
      <p>Files uploading in this session</p>
    </header>
    <ul>
      {uploadJobs.map((upload) => {
        const isActive =
          upload.status === DOCUMENT_STATUS.UPLOADING ||
          upload.status === DOCUMENT_STATUS.SAVING
        const hasFailed = upload.status === DOCUMENT_STATUS.FAILED
        const progress = upload.progress || 0
        const resumeText = hasFailed ? getResumeText(upload) : ''

        return (
          <li key={upload.id} title={upload.fileName}>
            <div className="upload-documents__progress-row">
              <div>
                <strong>{upload.fileName}</strong>
                <small
                  className={
                    hasFailed ? 'upload-documents__progress-error' : undefined
                  }
                >
                  {getStatusText({ ...upload, progress })}
                </small>
                {resumeText && <small>{resumeText}</small>}
              </div>
              {!isActive && (
                <div className="upload-documents__progress-actions">
                  {hasFailed && (
                    <button onClick={() => onRetry?.(upload.id)} type="button">
                      Retry
                    </button>
                  )}
                  <button
                    aria-label={`${upload.documentId ? 'Delete' : 'Dismiss'} ${upload.fileName}`}
                    disabled={deletingId === upload.documentId}
                    onClick={() =>
                      upload.documentId
                        ? onDelete(upload)
                        : onDismiss?.(upload.id)
                    }
                    type="button"
                  >
                    <Icon size="sm" svg={CloseIcon} />
                  </button>
                </div>
              )}
            </div>
            {isActive && (
              <div
                aria-label={`${upload.fileName} upload progress`}
                aria-valuemax="100"
                aria-valuemin="0"
                aria-valuenow={progress}
                className="upload-documents__progress-bar"
                role="progressbar"
              >
                <span style={{ width: `${progress}%` }} />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  </section>
)

const UploadDocumentsView = ({
  deletingId = '',
  documentTypes = [],
  existingFileNames = [],
  lifecycleStatus = BRAND_LIFECYCLE_STATUS.DRAFT,
  mismatchError = '',
  onDeleteDocument = () => {},
  onDismissUpload,
  onQueueUploads = () => {},
  onRetryUpload,
  uploadJobs = [],
  onOpenReview = () => {},
}) => {
  const [documentTypeId, setDocumentTypeId] = useState('')
  const [files, setFiles] = useState([])
  const [error, setError] = useState('')
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const hasDocumentType = Boolean(documentTypeId)
  const isExtractionInProgress =
    lifecycleStatus === BRAND_LIFECYCLE_STATUS.EXTRACTION_IN_PROGRESS
  const isInReview = lifecycleStatus === BRAND_LIFECYCLE_STATUS.IN_REVIEW

  const selectFiles = (selectedFiles) => {
    // A file has to be attributed to a document type, so the type comes first.
    if (!hasDocumentType) {
      setError('Select a document type before adding files.')
      return
    }
    const nextFiles = Array.from(selectedFiles || [])
    if (!nextFiles.length) return
    const oversizedFile = nextFiles.find(
      ({ size }) => size > MAX_FILE_SIZE_BYTES,
    )
    if (oversizedFile) {
      setError(`${oversizedFile.name} is larger than ${MAX_FILE_SIZE_MB} MB.`)
      return
    }
    const unsupportedFile = nextFiles.find(({ name }) => {
      const extension = name.slice(name.lastIndexOf('.')).toLowerCase()
      return !ACCEPTED_EXTENSIONS.includes(extension)
    })
    if (unsupportedFile) {
      setError(`${unsupportedFile.name} is not a supported file type.`)
      return
    }
    setFiles((current) => {
      const existingKeys = new Set(
        current.map(({ lastModified, name, size }) =>
          [name, size, lastModified].join('-'),
        ),
      )
      return [
        ...current,
        ...nextFiles.filter(
          ({ lastModified, name, size }) =>
            !existingKeys.has([name, size, lastModified].join('-')),
        ),
      ]
    })
    setError('')
  }

  // Hands the files off and resets straight away. The upload session panel
  // owns progress and failures from here, so the form stays free for the next
  // document instead of blocking on a transfer that may run for minutes.
  const addDocuments = () => {
    const documentType = documentTypes.find(({ id }) => id === documentTypeId)
    if (!documentType || !files.length) return

    setIsDuplicateModalOpen(false)
    onQueueUploads({ documentType, files })
    setError('')
    setDocumentTypeId('')
    setFiles([])
  }

  // Files whose names the brand already has are confirmed before uploading.
  const duplicateNames = files
    .map(({ name }) => name)
    .filter((name) => existingFileNames.includes(name))
  const startUpload = () =>
    duplicateNames.length > 0 ? setIsDuplicateModalOpen(true) : addDocuments()

  return (
    <section className="upload-documents">
      <ExtractionStatusNotice
        lifecycleStatus={lifecycleStatus}
        onOpenReview={onOpenReview}
      />
      {mismatchError && (
        <div
          className="upload-documents__notice upload-documents__notice--error"
          role="alert"
        >
          <Icon size="lg" svg={DatabaseIcon} />
          <span>
            <span>Unable to process: {mismatchError}</span>
            <strong>
              Please upload new relevant documents to proceed with new
              extraction.
            </strong>
          </span>
        </div>
      )}
      {!isExtractionInProgress && !isInReview && (
        <div className="upload-documents__notice">
          <Icon size="lg" svg={DatabaseIcon} />
          <span>
            <strong>Upload all documents before running AI extraction</strong>
            <span>
              Upload at least one document per required document type — you can
              add multiple files to the same type. Once all required types are
              covered, AI extraction unlocks.
            </span>
          </span>
        </div>
      )}

      {
        <div className="upload-documents__workspace">
          <div className="upload-documents__card">
            <header>
              <h2>Add document to queue</h2>
              <p>
                Select a document type and attach the file. Queued documents
                appear in the checklist on the right.
              </p>
            </header>

            <DocumentTypePicker
              documentTypes={documentTypes}
              onChange={(nextDocumentTypeId) => {
                setDocumentTypeId(nextDocumentTypeId)
                setError('')
              }}
              value={documentTypeId}
            />

            <div className="upload-documents__file">
              <span>File</span>
              <label
                aria-disabled={!hasDocumentType}
                className={
                  hasDocumentType ? undefined : 'upload-documents__file--locked'
                }
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault()
                  selectFiles(event.dataTransfer.files)
                }}
              >
                <input
                  accept={ACCEPTED_FILE_TYPES}
                  aria-label="Choose documents"
                  disabled={!hasDocumentType}
                  multiple
                  onChange={({ target }) => {
                    selectFiles(target.files)
                    target.value = ''
                  }}
                  type="file"
                />
                <Icon size="lg" svg={UploadIcon} />
                <strong>
                  {hasDocumentType
                    ? 'Drag & drop or click to select'
                    : 'Select a document type first'}
                </strong>
                <small>
                  {ACCEPTED_FILE_TYPES} — max {MAX_FILE_SIZE_MB} MB each
                </small>
              </label>
              {files.length > 0 && (
                <ul className="upload-documents__selected-files">
                  {files.map((selectedFile) => {
                    const fileKey = [
                      selectedFile.name,
                      selectedFile.size,
                      selectedFile.lastModified,
                    ].join('-')
                    return (
                      <li key={fileKey}>
                        <span className="upload-documents__selected-file-details">
                          <strong>{selectedFile.name}</strong>
                          <small>{formatFileSize(selectedFile.size)}</small>
                        </span>
                        <button
                          aria-label={`Remove ${selectedFile.name}`}
                          onClick={() =>
                            setFiles((current) =>
                              current.filter(
                                ({ lastModified, name, size }) =>
                                  [name, size, lastModified].join('-') !==
                                  fileKey,
                              ),
                            )
                          }
                          type="button"
                        >
                          <Icon size="sm" svg={CloseIcon} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {error && (
              <p className="upload-documents__error" role="alert">
                {error}
              </p>
            )}

            <div className="upload-documents__upload-row">
              <Button
                isDisabled={!hasDocumentType || !files.length}
                onClick={startUpload}
                variant="primary"
              >
                <span className="upload-documents__button-content">
                  <Icon size="sm" svg={UploadIcon} /> Upload
                </span>
              </Button>
            </div>
          </div>
          {uploadJobs.length > 0 && (
            <UploadProgress
              deletingId={deletingId}
              onDelete={onDeleteDocument}
              onDismiss={onDismissUpload}
              onRetry={onRetryUpload}
              uploadJobs={uploadJobs}
            />
          )}
        </div>
      }
      <DuplicateUploadModal
        duplicateNames={duplicateNames}
        fileCount={files.length}
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onConfirm={addDocuments}
      />
    </section>
  )
}

export default UploadDocumentsView
